import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { KIND } from "baseui/button";
import { ConfirmDialog } from "components/confirm-dialog";
import { Content } from "components/content";
import { FormRenderer } from "components/form-renderer";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { useAuth } from "contexts/auth-context";
import { useDictionaries } from "contexts/dictionaries-context";
import { useLoading } from "contexts/loading-context";
import { useProject } from "contexts/project-context";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import { DeviceFloppy, FileOff } from "tabler-icons-react";
import { PERMISSIONS } from "utils/permissions";

import { Tag } from "../tags";
import { TAGS_FIELDS, TagsFormInputs } from "../tags.form";
import { TAGS_SHOW, TAGS_UPDATE } from "../tags.gql";

export default function TagsUpdate(): React.ReactElement {
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] = useState(
    false
  );
  const { enqueueSnackbar } = useSnackbar();
  const { setIsFetching, isLoading, setIsLoading } = useLoading();
  const { checkPermission } = useAuth();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const { activeProject, activeProjectId } = useProject();
  const { findValue } = useDictionaries();
  const [originalProjectId, setOriginalProjectId] = useState<number>();

  const {
    control,
    formState: { errors, isDirty, ...formState },
    handleSubmit,
    setValue,
    reset,
    watch,
    ...methods
  } = useForm<TagsFormInputs>();

  const { refetch, data, error: queryError } = useQuery(TAGS_SHOW, {
    variables: { id: id ? parseInt(id) : null },
  });
  const tag: Tag = data?.tag;

  useEffect(() => {
    setIsFetching(true);
    if (data?.tag) refetch();
  }, []);

  useEffect(() => {
    if (queryError?.graphQLErrors)
      enqueueSnackbar({
        message: (queryError as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
  }, [queryError]);

  useEffect(() => {
    if (data?.tag) {
      setIsFetching(false);

      setOriginalProjectId(tag?.project?.id);

      setValue("project", [{ ...tag?.project, id: tag?.project?.id }]);
      setValue("language", [{ id: tag?.language }]);
      setValue("name", tag?.name);
      setValue("isVisible", tag?.isVisible);
      setValue("slug", tag?.slug);
    }
  }, [data]);

  useEffect(() => {
    !activeProject &&
      setValue("project", [{ ...tag?.project, id: tag?.project?.id }]);
  }, [activeProject]);

  const watchProject = watch("project");

  useEffect(() => {
    if (
      originalProjectId === watchProject?.[0]?.id ||
      originalProjectId === activeProjectId
    )
      setValue("language", [
        {
          id: tag?.language,
          label: (findValue(tag?.language as string) as unknown) as string,
        },
      ]);
    else setValue("language", []);
  }, [watchProject, activeProject]);

  const [updateTag, { error }] = useMutation(TAGS_UPDATE);

  const onSubmit = async ({
    project,
    language,
    isVisible,
    ...values
  }: TagsFormInputs): Promise<void> => {
    setIsLoading(true);

    try {
      await updateTag({
        variables: {
          tagUpdateInput: {
            id: id ? parseInt(id) : null,
            projectId: activeProjectId || project?.[0]?.id,
            language: language?.[0]?.id,
            isVisible: !!isVisible,
            ...values,
          },
        },
      });
      enqueueSnackbar({
        message: "Zapisano pomyślnie",
        variant: "success",
      });
      history.push(`/tags/${id}`);
    } catch (error: unknown) {
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!checkPermission(PERMISSIONS.tag.update))
    return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title={`${tag?.name}`}
        labels={["Tagi", "Edytowanie"]}
        goBackOption
        buttons={[
          {
            label: "Anuluj",
            kind: KIND.secondary,
            startEnhancer: <FileOff size={18} />,
            disabled: isLoading,
            onClick: () =>
              isDirty ? setIsCancelConfirmDialogOpen(true) : history.goBack(),
          },
          {
            label: "Zapisz",
            kind: KIND.primary,
            startEnhancer: <DeviceFloppy size={18} />,
            onClick: handleSubmit(onSubmit),
            disabled: isLoading,
            isLoading: isLoading,
            type: "submit",
            formId: "updateTag",
          },
        ]}
      />
      <Content>
        <FormProvider<TagsFormInputs>
          control={control}
          formState={{ errors, isDirty, ...formState }}
          handleSubmit={handleSubmit}
          reset={reset}
          setValue={setValue}
          watch={watch}
          {...methods}
        >
          <FormRenderer<TagsFormInputs>
            id="updateTag"
            onSubmit={handleSubmit(onSubmit)}
            type="update"
            isLoading={isLoading}
            error={error}
            fields={TAGS_FIELDS}
          />
        </FormProvider>
        <ConfirmDialog
          isOpen={isCancelConfirmDialogOpen}
          label="Anulowanie edycji tagu"
          close={() => setIsCancelConfirmDialogOpen(false)}
          confirm={() => history.goBack()}
        />
      </Content>
    </article>
  );
}
